import React, { useState, useEffect } from 'react';
import { FiX as X, FiCopy as Copy, FiCheck as Check, FiCheckCircle as CheckCircle2, FiXCircle as XCircle } from 'react-icons/fi';
import { getGuardrailEvaluationDetails } from '../../../services/cloudopsApi';
import ErrorDisplay from '../../../components/ErrorDisplay';

interface EvaluationData {
  guardrail_name: string;
  guardrail_id: string;
  resource_id: string;
  violation_message?: string | null;
  resource_data: Record<string, unknown>;
  evaluated_attributes?: Record<string, unknown>;
}

interface EvaluationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  scanId: string;
  resourceId: string;
  guardrailId: string;
  guardrailName?: string;
}

const EvaluationDetailsModal: React.FC<EvaluationDetailsModalProps> = ({
  isOpen,
  onClose,
  scanId,
  resourceId,
  guardrailId,
  guardrailName,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [evaluationData, setEvaluationData] = useState<EvaluationData | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && scanId && resourceId && guardrailId) {
      loadEvaluationDetails();
    } else {
      // Reset state when modal closes
      setEvaluationData(null);
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, scanId, resourceId, guardrailId]);

  const loadEvaluationDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getGuardrailEvaluationDetails(scanId, resourceId, guardrailId);
      setEvaluationData(data);
    } catch (err: unknown) {
      console.error('Error loading evaluation details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load evaluation details');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string): void => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatAttributePath = (path: string): string => {
    return path
      .split('.')
      .map((segment) => {
        // Capitalize first letter of each segment
        return segment.charAt(0).toUpperCase() + segment.slice(1);
      })
      .join(' → ');
  };

  const highlightEvaluatedAttributes = (jsonString: string, evaluatedPaths: string[]): string => {
    // Create a simple highlighting approach
    // For each evaluated path, find and mark the key in the JSON
    let highlighted = jsonString;
    
    for (const path of evaluatedPaths) {
      const segments = path.split('.');
      const lastSegment = segments[segments.length - 1];

      // Create regex to match the key in JSON (handles quotes and spacing)
      // Escape special regex characters in the segment name
      const escapedSegment = lastSegment.replaceAll(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`"${escapedSegment}"\\s*:`, 'g');
      highlighted = highlighted.replace(
        regex,
        (match) => `<mark style="background-color: rgba(250, 204, 21, 0.3); padding: 2px 4px; border-radius: 3px;">${match}</mark>`
      );
    }

    return highlighted;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col transition-colors">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10 transition-colors">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors flex items-center gap-2">
              Evaluation Details:{' '}
              {evaluationData ? (
                <>
                  {evaluationData.violation_message ? (
                    <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                  )}
                  {evaluationData.guardrail_name} {evaluationData.violation_message ? 'Test Failed' : 'Test Passed'}
                </>
              ) : (
                guardrailName || 'Guardrail Evaluation'
              )}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Resource ID: <code className="text-xs">{resourceId}</code>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors ml-4"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <ErrorDisplay
              error={error}
              onRetry={loadEvaluationDetails}
              title="Failed to Load Evaluation Details"
            />
          ) : evaluationData ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column: Full Resource JSON */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                    Resource Data
                  </h4>
                  <button
                    onClick={() => handleCopy(JSON.stringify(evaluationData.resource_data, null, 2))}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy JSON
                      </>
                    )}
                  </button>
                </div>
                {Object.keys(evaluationData.evaluated_attributes || {}).length > 0 && (
                  <div className="text-xs text-gray-600 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded px-3 py-2">
                    <span className="font-semibold">Note:</span> Highlighted keys indicate attributes that were evaluated by this guardrail.
                  </div>
                )}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto border border-gray-200 dark:border-gray-700">
                  <pre
                    className="text-xs text-gray-800 dark:text-gray-200 font-mono whitespace-pre-wrap break-words"
                    dangerouslySetInnerHTML={{
                      __html: highlightEvaluatedAttributes(
                        JSON.stringify(evaluationData.resource_data, null, 2),
                        Object.keys(evaluationData.evaluated_attributes || {})
                      ),
                    }}
                  />
                </div>
              </div>

              {/* Right Column: Evaluated Attributes */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                  Evaluated Attributes
                </h4>

                {/* Violation Message */}
                {evaluationData.violation_message && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                          {evaluationData.guardrail_name} Test Failed
                        </p>
                        <p className="text-sm text-red-700 dark:text-red-300">
                          {evaluationData.violation_message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {!evaluationData.violation_message && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                          {evaluationData.guardrail_name} Test Passed
                        </p>
                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                          All evaluated attributes meet the guardrail requirements.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Evaluated Attributes List */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <div className="space-y-3">
                    {Object.entries(evaluationData.evaluated_attributes || {}).map(
                      ([path, value]) => (
                        <div
                          key={path}
                          className="border-b border-gray-200 dark:border-gray-700 last:border-b-0 pb-3 last:pb-0"
                        >
                          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            {formatAttributePath(path)}
                          </div>
                          <div className="text-sm text-gray-900 dark:text-white font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-200 dark:border-gray-700">
                            {typeof value === 'object' ? (
                              JSON.stringify(value, null, 2)
                            ) : (
                              String(value)
                            )}
                          </div>
                        </div>
                      )
                    )}
                    {(!evaluationData.evaluated_attributes ||
                      Object.keys(evaluationData.evaluated_attributes).length === 0) && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 text-center py-4">
                        No evaluated attributes available
                      </p>
                    )}
                  </div>
                </div>

                {/* Guardrail Info */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                    Guardrail Information
                  </h5>
                  <div className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
                    <div>
                      <span className="font-medium">Name:</span>{' '}
                      {evaluationData.guardrail_name}
                    </div>
                    <div>
                      <span className="font-medium">Guardrail ID:</span>{' '}
                      <code className="text-xs">{evaluationData.guardrail_id}</code>
                    </div>
                    <div>
                      <span className="font-medium">Resource ID:</span>{' '}
                      <code className="text-xs">{evaluationData.resource_id}</code>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EvaluationDetailsModal;

